import { useLingui } from '@lingui/react/macro';
import { Calendar, Clock, Copy, Pencil, Trash2, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { detectDayPattern, scheduleToDays } from '@/domain/templates/day-of-week';
import { getShiftColour, SHIFT_PILL_CLASSES } from '@/domain/templates/shift-colours';
import { resolveInstanceCount } from '@/domain/templates/staffing';
import { cn } from '@/lib/utils';
import type { ShiftSet } from '@/types/template';

interface PatternCardProps {
  shift: ShiftSet;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

/**
 * A saved shift pattern in the Build workspace's left pane, rendered as its own
 * card: the shift name (falling back to the type) with a shift-type badge, a
 * `days · time` line, a `heads · staffing method` line, and row actions.
 */
export function PatternCard({ shift, onEdit, onDuplicate, onDelete }: PatternCardProps) {
  const { t } = useLingui();
  const heads = resolveInstanceCount(shift);
  const scheduleDays = scheduleToDays(shift.schedule);
  const swatch = SHIFT_PILL_CLASSES[getShiftColour(shift.shiftType.name)].swatch;

  const name = shift.name?.trim();
  const typeName = shift.shiftType.name;
  const title = name || typeName || t`Untitled shift`;
  // Only badge the type when it isn't already the title (i.e. a real name exists).
  const showTypeBadge = Boolean(name) && Boolean(typeName);

  // Labels are computed in component scope so the Lingui `t` macro transforms
  // them — it does not reliably transform `t` passed into a module-level helper.
  const dayCount = scheduleDays.length;
  const days = ((): string => {
    switch (detectDayPattern(scheduleDays)) {
      case 'weekdays':
        return t`Weekdays`;
      case 'weekends':
        return t`Weekends`;
      case 'everyday':
        return t`Every day`;
      default:
        return dayCount === 1 ? t`1 day` : t`${dayCount} days`;
    }
  })();
  const totalShifts = heads * dayCount;
  const shiftsLabel = totalShifts === 1 ? t`1 shift` : t`${totalShifts} shifts`;
  const peopleLabel = heads === 1 ? t`1 person` : t`${heads} people`;
  const methodLabel = ((): string => {
    switch (shift.staffing.method) {
      case 'specific':
        return t`Specific person`;
      case 'open':
        return t`Open shift`;
      default:
        return t`Auto-assign`;
    }
  })();

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border bg-card p-3 transition-colors hover:border-primary">
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-center gap-2">
          <span
            data-testid="pattern-swatch"
            className={cn('size-2.5 shrink-0 rounded-[3px] border', swatch)}
          />
          <span className="truncate font-medium">{title}</span>
          {showTypeBadge && <Badge variant="secondary">{typeName}</Badge>}
        </div>
        <div
          data-testid="pattern-timing"
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <span className="inline-flex items-center gap-1">
            <Calendar className="size-3.5" />
            {days}
          </span>
          <span className="size-1 rounded-full bg-muted-foreground/50" />
          <span className="inline-flex items-center gap-1">
            <Clock className="size-3.5" />
            {shift.startTime}–{shift.endTime}
          </span>
          <span className="size-1 rounded-full bg-muted-foreground/50" />
          <span>{shiftsLabel}</span>
        </div>
        <div
          data-testid="pattern-staffing"
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Users className="size-3.5" />
          {peopleLabel} · {methodLabel}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="icon" aria-label={t`Edit`} onClick={onEdit}>
          <Pencil className="size-4" />
        </Button>
        <Button variant="ghost" size="icon" aria-label={t`Duplicate`} onClick={onDuplicate}>
          <Copy className="size-4" />
        </Button>
        <Button variant="ghost" size="icon" aria-label={t`Delete`} onClick={onDelete}>
          <Trash2 className="size-4" />
        </Button>
      </div>
    </div>
  );
}
